import { FC, useEffect } from "react";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import InsurancePackage from "./InsurancePackage";
import { Deductible, Limit } from "components-shared/insuranceData";
import {
  Package,
  PackageConfig,
} from "components/ContentfulRenderer/ContentfulPackages/types";
import "swiper/css";
import Tabs from "ui-kit/Tabs/Tabs";
import GrandPrice from "components/Signup/components/GrandPrice";
import { AmountToPay } from "utils/insurance/getAmountToPay";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import { useWebTranslation } from "translations";
import Gutter from "ui-kit/Gutter";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import { Typography } from "ui-kit/Typography";
import useMediaQuery from "hooks/useMediaQuery";
import { CampaignBadge } from "../CampaignBadge/CampaignBadge";
import { Container } from "ui-kit/Layout";
import { isOrg } from "utils/site";
import { InsurancePackage as InsurancePackageV2 } from "./InsurancePackageV2";
import { Insurance } from "components/Signup/store/state/types";

type InsurancePackagesProps = {
  packageConfig: PackageConfig;
  packagePrices: AmountToPay[];
  selectedIndex: number;
  onChange: ({
    selectedLimitAddon,
    limit,
    deductible,
    pack,
  }: {
    selectedLimitAddon?: number;
    limit: Limit;
    deductible?: Deductible;
    pack?: Package;
  }) => void;
  onCompare: () => void;
  numberOfHighlightItems?: number;
  numberOfVisibleItems?: number;
  isCopyExperiment?: boolean;
  insurance?: Insurance;
};

const SwiperController = ({
  packageConfig,
  selectedIndex,
  onChange,
}: Pick<
  InsurancePackagesProps,
  "packageConfig" | "selectedIndex" | "onChange"
>) => {
  const { id } = useCurrentAnimal();
  const { selectedLimitAddon } = useCurrentInsuranceByAnimalId(id);
  const swiper = useSwiper();
  const { t } = useWebTranslation();

  useEffect(() => {
    swiper.slideTo(selectedIndex);
  }, [selectedIndex, swiper]);

  return (
    <Tabs
      items={[]}
      tabs={packageConfig.packages.map((pack) => ({
        value: pack.title,
        label: t(pack.title),
      }))}
      onChangeTabs={(_, index) => {
        swiper.slideTo(index);
        const newLimit = packageConfig.packages[index].limitAddons?.includes(
          selectedLimitAddon ?? 0
        )
          ? selectedLimitAddon
          : packageConfig.packages[index].limit;

        onChange({
          limit: newLimit as Limit,
          deductible: packageConfig.packages[index].deductible,
          pack: packageConfig.packages[index],
        });
      }}
      index={selectedIndex}
    />
  );
};

const InsurancePackages: FC<InsurancePackagesProps> = ({
  packageConfig,
  packagePrices,
  selectedIndex,
  onChange,
  onCompare,
  numberOfHighlightItems,
  numberOfVisibleItems,
  isCopyExperiment,
  insurance,
}) => {
  const { t } = useWebTranslation();
  const { isDesktop } = useMediaQuery();
  const { name, id } = useCurrentAnimal();
  const { drafts, billingInterval, limit, selectedLimitAddon } =
    useCurrentInsuranceByAnimalId(id);

  return (
    <Container wide={isDesktop}>
      <FlexCol alignItems="center">
        <Gutter size={3} />
        <Typography variant="body2">
          <strong>
            {t("global.insurance_animal", {
              name: name ?? "",
            })}
            {/* {t("quote_preview.pet_insurance", { name: name ?? "" })} */}
          </strong>
        </Typography>
        <Gutter size={1} />
        <GrandPrice
          amountToPay={packagePrices[selectedIndex]}
          billingInterval={billingInterval}
        />
        <Gutter size={1} />
        <CampaignBadge fees={drafts?.[0].fees} />
      </FlexCol>
      <Gutter size={3} />
      {!isDesktop ? (
        <Swiper
          slideToClickedSlide={true}
          slidesPerView={1.17}
          spaceBetween={12}
          initialSlide={selectedIndex}
          onSlideChange={(e) => {
            const pack = packageConfig.packages[e.activeIndex];

            const newLimit = pack.limitAddons?.includes(selectedLimitAddon ?? 0)
              ? selectedLimitAddon
              : pack.limit;

            onChange({
              limit: (newLimit ?? pack.limit) as Limit,
              deductible: pack.deductible,
            });
          }}
          style={{
            margin: "0 -16px",
          }}
          centeredSlides
        >
          <div slot="container-start" style={{ margin: "0 16px" }}>
            <SwiperController
              packageConfig={packageConfig}
              selectedIndex={selectedIndex}
              onChange={({ limit, selectedLimitAddon, pack }) => {
                if (!pack) {
                  return;
                }

                const newLimit = pack.limitAddons?.includes(
                  selectedLimitAddon ?? 0
                )
                  ? selectedLimitAddon
                  : limit;

                onChange({
                  limit: (newLimit ?? pack.limit) as Limit,
                  deductible: pack.deductible,
                });
              }}
            />
            <Gutter size={3} />
          </div>
          {packageConfig.packages.map((pack, index) => {
            const isActive =
              pack.limitAddons?.includes(limit) || index === selectedIndex;

            if (isOrg("swe")) {
              return (
                <SwiperSlide key={pack.title} style={{ height: "auto" }}>
                  <div style={{ display: "flex", height: "100%" }}>
                    <InsurancePackageV2
                      key={pack.title}
                      pack={pack}
                      product={packageConfig.product}
                      selectedLimitAddon={selectedLimitAddon}
                      onPressLimitAddon={(limit) => {
                        onChange({
                          selectedLimitAddon: limit,
                          limit: limit as Limit,
                        });
                      }}
                      isActive={isActive}
                      onCompare={onCompare}
                      insurance={insurance}
                    />
                  </div>
                </SwiperSlide>
              );
            }

            return (
              <SwiperSlide key={pack.title} style={{ height: "auto" }}>
                <InsurancePackage
                  packageConfig={packageConfig}
                  pack={pack}
                  active={isActive}
                  onCompare={onCompare}
                  style={{ height: "100%" }}
                  numberOfHighlightItems={numberOfHighlightItems}
                  numberOfVisibleItems={numberOfVisibleItems}
                  isCopyExperiment={isCopyExperiment}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      ) : (
        <FlexRow
          justifyContent="center"
          style={{ ...(isOrg("swe") && { gap: 8 }) }}
        >
          {packageConfig.packages.map((pack, index) => {
            const isActive =
              pack.limitAddons?.includes(limit) || index === selectedIndex;

            if (isOrg("swe")) {
              return (
                <InsurancePackageV2
                  key={pack.title}
                  style={{ display: "flex", flex: 1 }}
                  pack={pack}
                  product={packageConfig.product}
                  selectedLimitAddon={selectedLimitAddon}
                  onPress={() => {
                    const newLimit = pack.limitAddons?.includes(
                      selectedLimitAddon ?? 0
                    )
                      ? selectedLimitAddon
                      : pack.limit;

                    onChange({
                      limit: (newLimit ?? pack.limit) as Limit,
                      deductible: packageConfig.packages[index].deductible,
                    });
                  }}
                  onPressLimitAddon={(limit) =>
                    onChange({
                      selectedLimitAddon: limit,
                      limit: limit as Limit,
                    })
                  }
                  isActive={isActive}
                  onCompare={onCompare}
                  insurance={insurance}
                />
              );
            }

            return (
              <InsurancePackage
                key={pack.title}
                packageConfig={packageConfig}
                pack={pack}
                amountToPay={packagePrices[index]}
                active={isActive}
                onPress={() => {
                  const newLimit = pack.limitAddons?.includes(
                    selectedLimitAddon ?? 0
                  )
                    ? selectedLimitAddon
                    : pack.limit;

                  onChange({
                    limit: (newLimit ?? pack.limit) as Limit,
                    deductible: packageConfig.packages[index].deductible,
                  });
                }}
                onCompare={onCompare}
                style={{
                  width: 320,
                  margin: "0 8px",
                  marginTop: pack.popular ? 0 : 24,
                }}
                numberOfHighlightItems={numberOfHighlightItems}
                numberOfVisibleItems={numberOfVisibleItems}
                isCopyExperiment={isCopyExperiment}
              />
            );
          })}
        </FlexRow>
      )}
    </Container>
  );
};

export default InsurancePackages;
